import { useState, useCallback, useEffect, useMemo } from 'react';

const IGNORE_RESET_MS = 600;

/** Where the last chosen provider/model pair is remembered across reloads. */
export const CHAT_MODEL_STORAGE_KEY = 'aimsos.chat.model';

/**
 * Reads the remembered pair, discarding anything the current provider list no longer offers
 * (a model can be renamed or dropped between releases — a stale value would otherwise leave the
 * selector showing a model the backend cannot serve).
 *
 * @param {string} storageKey
 * @param {Array} providers
 * @returns {{ provider: string, model: string } | null}
 */
const readStoredSelection = (storageKey, providers) => {
  if (typeof window === 'undefined') return null;
  let raw = null;
  try {
    raw = window.localStorage.getItem(storageKey);
  } catch {
    // Private mode / blocked storage — fall back to the default selection.
    return null;
  }
  if (!raw) return null;

  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const { provider, model } = parsed;
  if (typeof provider !== 'string' || typeof model !== 'string') return null;

  // Nothing to validate against yet (providers still loading) — trust the stored pair.
  if (!providers.length) return { provider, model };

  const entry = providers.find((p) => p.provider === provider);
  if (!entry?.models?.some((m) => m.value === model)) return null;

  return { provider, model };
};

/**
 * Hook for chat model/provider selector state.
 * State lives in context (via HomeChatProviderWrapper), so selection persists across AgentView / ConversationView,
 * and is mirrored to localStorage so it also survives a page reload (CT-5299).
 * Ignores a spurious onModelChange to the default (first) model when it happens right after send,
 * to avoid ChatInput (in the newly mounted ConversationView) resetting the selection.
 *
 * @param {Array} providersList - Array of { provider, label, models: [{ value, label }] }
 * @param {{ lastSendTimeRef?: React.MutableRefObject<number | null>, storageKey?: string }} options - Optional.
 *   lastSendTimeRef: ref updated with Date.now() when a message is sent.
 *   storageKey: override the localStorage key (e.g. to scope the memory per surface).
 * @returns {Object} { providers, selectedProvider, selectedModel, onModelChange, onProviderChange }
 */
export const useChatModelSelector = (providersList = [], options = {}) => {
  const { lastSendTimeRef, storageKey = CHAT_MODEL_STORAGE_KEY } = options;
  // Memoised so the `[]` fallback is not a fresh array each render, which would change the identity
  // of every callback below on every render.
  const providers = useMemo(
    () => (Array.isArray(providersList) ? providersList : []),
    [providersList],
  );

  const firstProvider = providers[0];
  const firstModel = firstProvider?.models?.[0]?.value;

  // Lazy initialiser: storage is read once, on the first render, so the selector never flashes the
  // default before restoring. Provider and model are one value so the pair can never half-update.
  const [selection, setSelection] = useState(() => {
    const stored = readStoredSelection(storageKey, providers);
    return {
      provider: stored?.provider ?? firstProvider?.provider ?? null,
      model: stored?.model ?? firstModel ?? null,
    };
  });
  const { provider: selectedProvider, model: selectedModel } = selection;

  const onProviderChange = useCallback((provider) => {
    const providerEntry = providers.find((p) => p.provider === provider);
    setSelection({
      provider,
      model: providerEntry?.models?.[0]?.value ?? null,
    });
  }, [providers]);

  const onModelChange = useCallback(
    (modelValue) => {
      setSelection((prev) => {
        if (prev.model === modelValue) return prev;
        // Evitar reset espurio: al enviar el primer mensaje se monta ConversationView y su ChatInput
        // puede llamar onModelChange con el valor por defecto (primer modelo). Ignorar ese cambio
        // si ocurre justo después de enviar.
        if (
          lastSendTimeRef?.current != null &&
          firstModel != null &&
          modelValue === firstModel &&
          prev.model !== firstModel
        ) {
          const elapsed = Date.now() - lastSendTimeRef.current;
          if (elapsed < IGNORE_RESET_MS) return prev;
        }
        // The selector reports the model, not the provider, so derive the owning provider from the
        // chosen model rather than trusting the two to arrive together.
        const owner = providers.find((p) => p.models?.some((m) => m.value === modelValue));
        return { provider: owner?.provider ?? prev.provider, model: modelValue };
      });
    },
    [firstModel, lastSendTimeRef, providers]
  );

  // Mirror the live selection. A rejected spurious change returns the previous state, so no
  // render happens and nothing is written — the remembered pair stays the one the user picked.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!selectedProvider || !selectedModel) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ provider: selectedProvider, model: selectedModel })
      );
    } catch {
      // Storage unavailable — selection still works for this session.
    }
  }, [storageKey, selectedProvider, selectedModel]);

  return useMemo(
    () => ({
      providers,
      selectedProvider,
      selectedModel,
      onModelChange,
      onProviderChange,
    }),
    [
      providers,
      selectedProvider,
      selectedModel,
      onModelChange,
      onProviderChange,
    ]
  );
};
